import api, { ApiResponse, PaginatedApiResponse } from './axiosInstance';
import { Location, CreateLocationData, UpdateLocationData, LocationFilters } from '../types/location';

// Re-export types for convenience
export type { Location, CreateLocationData, UpdateLocationData, LocationFilters };

class LocationService {
  private baseUrl = '/locations';

  // Normalize location data
  private normalizeLocation = (raw: any): Location => {
    const createdBy =
      raw && typeof raw.createdBy === 'object' && raw.createdBy !== null
        ? (raw.createdBy.id ?? raw.createdBy._id ?? '')
        : raw?.createdBy ?? '';
    const updatedBy =
      raw && typeof raw.updatedBy === 'object' && raw.updatedBy !== null
        ? (raw.updatedBy.id ?? raw.updatedBy._id ?? '')
        : raw?.updatedBy ?? '';

    return {
      _id: raw?._id ?? raw?.id ?? '',
      id: raw?.id ?? raw?._id,
      locationNumber: raw?.locationNumber ?? 0,
      name: raw?.name ?? '',
      code: raw?.code ?? '',
      type: raw?.type ?? 'Other',
      status: raw?.status ?? 'active',
      isActive: raw?.isActive ?? (raw?.status === 'active'),
      
      // NetSuite Integration Fields
      stateLocation: raw?.stateLocation,
      subLocation: raw?.subLocation,
      locationId: raw?.locationId,
      locationName: raw?.locationName,
      
      // Address information
      address: raw?.address,
      city: raw?.city,
      state: raw?.state,
      country: raw?.country,
      zipCode: raw?.zipCode,
      mainAddress: raw?.mainAddress,
      latitude: raw?.latitude,
      longitude: raw?.longitude,
      
      // Location characteristics
      accessibility: raw?.accessibility,
      nearestCity: raw?.nearestCity,
      distanceFromNearestCity: raw?.distanceFromNearestCity,
      
      // Connectivity and utilities
      connectivity: raw?.connectivity,
      utilities: raw?.utilities,
      
      // Additional fields
      description: raw?.description,
      notes: raw?.notes,
      capacity: raw?.capacity,
      environmental: raw?.environmental,
      
      // Audit fields
      createdAt: raw?.createdAt ?? '',
      updatedAt: raw?.updatedAt ?? '',
      createdBy,
      updatedBy,
    } as Location;
  };

  /**
   * Get all locations with pagination and filtering
   */
  async getLocations(filters: LocationFilters = {}): Promise<PaginatedApiResponse<Location>> {
    try {
      const params: Record<string, unknown> = {};

      if (filters.search) params.search = filters.search;
      if (filters.type) params.type = filters.type;
      if (filters.locationStatus) params['location-status'] = filters.locationStatus;
      // Only send status when explicitly 'active' or 'inactive'. Omit for 'all' so backend returns both.
      if (filters.status === 'active' || filters.status === 'inactive') {
        params.status = filters.status;
      }
      if (filters.accessibility) params.accessibility = filters.accessibility;
      if (filters.state) params.state = filters.state;
      if (filters.city) params.city = filters.city;
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.sortBy && filters.sortOrder) {
        params['sort-by'] = filters.sortBy;
        params['sort-order'] = filters.sortOrder;
      }

      const response = await api.get<PaginatedApiResponse<any>>(this.baseUrl, {
        params,
        headers: { 'Cache-Control': 'no-cache' },
      });
      const normalized: PaginatedApiResponse<Location> = {
        ...response.data,
        data: (response.data?.data ?? []).map((l: any) => this.normalizeLocation(l)),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error fetching locations:', error);
      throw new Error(error.message || 'Failed to fetch locations');
    }
  }

  /**
   * Get a single location by ID
   */
  async getLocationById(id: string): Promise<ApiResponse<Location>> {
    try {
      const response = await api.get<ApiResponse<any>>(`${this.baseUrl}/${id}`);
      const normalized: ApiResponse<Location> = {
        ...response.data,
        data: this.normalizeLocation(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error fetching location:', error);
      throw new Error(error.message || 'Failed to fetch location');
    }
  }

  /**
   * Create a new location
   */
  async createLocation(data: CreateLocationData): Promise<ApiResponse<Location>> {
    try {
      const response = await api.post<ApiResponse<any>>(this.baseUrl, data);
      const normalized: ApiResponse<Location> = {
        ...response.data,
        data: this.normalizeLocation(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error creating location:', error);
      throw new Error(error.message || 'Failed to create location');
    }
  }

  /**
   * Update a location
   */
  async updateLocation(id: string, data: UpdateLocationData): Promise<ApiResponse<Location>> {
    try {
      const response = await api.put<ApiResponse<any>>(`${this.baseUrl}/${id}`, data);
      const normalized: ApiResponse<Location> = {
        ...response.data,
        data: this.normalizeLocation(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error updating location:', error);
      throw new Error(error.message || 'Failed to update location');
    }
  }

  /**
   * Activate a location
   */
  async activateLocation(id: string): Promise<ApiResponse<Location>> {
    try {
      const response = await api.patch<ApiResponse<any>>(`${this.baseUrl}/${id}/activate`);
      const normalized: ApiResponse<Location> = {
        ...response.data,
        data: this.normalizeLocation(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error activating location:', error);
      throw new Error(error.message || 'Failed to activate location');
    }
  }

  /**
   * Inactivate (soft delete) a location
   */
  async inactivateLocation(id: string): Promise<ApiResponse<Location>> {
    try {
      const response = await api.patch<ApiResponse<any>>(`${this.baseUrl}/${id}/inactivate`);
      const normalized: ApiResponse<Location> = {
        ...response.data,
        data: this.normalizeLocation(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error inactivating location:', error);
      throw new Error(error.message || 'Failed to inactivate location');
    }
  }

  /**
   * Delete a location (hard delete)
   */
  async deleteLocation(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete<ApiResponse<any>>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting location:', error);
      throw new Error(error.message || 'Failed to delete location');
    }
  }

  /**
   * Search locations
   */
  async searchLocations(searchTerm: string, filters?: Omit<LocationFilters, 'search'>): Promise<PaginatedApiResponse<Location>> {
    try {
      const searchFilters = { ...filters, search: searchTerm };
      return this.getLocations(searchFilters);
    } catch (error: any) {
      console.error('Error searching locations:', error);
      throw new Error(error.message || 'Failed to search locations');
    }
  }

  /**
   * Get active locations only
   */
  async getActiveLocations(filters?: Omit<LocationFilters, 'status'>): Promise<PaginatedApiResponse<Location>> {
    try {
      const searchFilters = { ...filters, status: 'active' as const };
      return this.getLocations(searchFilters);
    } catch (error: any) {
      console.error('Error fetching active locations:', error);
      throw new Error(error.message || 'Failed to fetch active locations');
    }
  }

  /**
   * Get locations for dropdown (simplified)
   */
  async getLocationsForDropdown() {
    try {
      const response = await this.getLocations({ limit: 100, status: 'active' });
      return response.data.map((location: Location) => ({
        _id: location._id,
        id: location.id,
        name: location.name,
        code: location.code,
        type: location.type,
        city: location.city,
        state: location.state,
        locationName: location.locationName
      }));
    } catch (error: any) {
      console.error('Error fetching locations for dropdown:', error);
      return [];
    }
  }

  /**
   * Get all locations with pagination support (for legacy compatibility)
   */
  async getAllLocations() {
    try {
      let allLocations: any[] = [];
      let page = 1;
      let hasMoreData = true;

      while (hasMoreData) {
        const response = await this.getLocations({ limit: 100, status: 'active', page });

        if (response.success && response.data) {
          allLocations = [...allLocations, ...response.data];

          // Check if there are more pages
          const meta = response.meta;
          if (meta && meta.page < meta.totalPages) {
            page++;
          } else {
            hasMoreData = false;
          }
        } else {
          hasMoreData = false;
        }
      }

      return allLocations;
    } catch (error) {
      console.error('Error fetching all locations:', error);
      // Fallback to simple approach
      return this.getLocationsForDropdown();
    }
  }
}

export const locationService = new LocationService();
export default locationService;